import React, { useState } from 'react';
import {
  Box, Card, CardContent, TextField, Button, Typography, Alert,
  InputAdornment, IconButton, CircularProgress, Divider, Stack, Checkbox, FormControlLabel,
} from '@mui/material';
import { GraphicEq, Visibility, VisibilityOff, Lock, ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuthStore } from '../store/auth';

type Step = 'credentials' | 'mfa' | 'setup';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const setAuth = useAuthStore(s => s.setAuth);

  const [step, setStep] = useState<Step>('credentials');
  const [form, setForm] = useState({ email: '', password: '' });
  const [mfaToken, setMfaToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [setupToken, setSetupToken] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [setupSecret, setSetupSecret] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [trustDevice, setTrustDevice] = useState(true);

  // Trusted-device tokens are kept per login name, so several accounts can each be trusted on
  // this browser (the server only accepts a token for the account it was issued to).
  const deviceTokens = (): Record<string, string> => { try { return JSON.parse(localStorage.getItem('device_tokens') || '{}'); } catch { return {}; } };
  const loginKey = () => String(form.email || '').trim().toLowerCase();

  const handleCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/auth/login', { ...form, device_token: deviceTokens()[loginKey()] || localStorage.getItem('device_token') || undefined });
      if (data.mfa_required) {
        setMfaToken(data.mfa_token);
        setStep('mfa');
      } else if (data.mfa_setup_required) {
        // Admin has required 2FA for this account — force enrolment before access
        setSetupToken(data.setup_token);
        const { data: s } = await api.post('/auth/mfa/setup-forced', { setup_token: data.setup_token });
        setQrCode(s.qr_code); setSetupSecret(s.secret); setMfaCode('');
        setStep('setup');
      } else {
        setAuth(data.user, data.access_token);
        navigate('/dashboard');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleEnableForced = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mfaCode.replace(/\s/g, '').length !== 6) { setError('Enter the 6-digit code from your app'); return; }
    setLoading(true); setError('');
    try {
      const { data } = await api.post('/auth/mfa/enable-forced', {
        setup_token: setupToken, code: mfaCode.replace(/\s/g, ''),
      });
      setAuth(data.user, data.access_token);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid code — try again');
    } finally {
      setLoading(false);
    }
  };

  const handleMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mfaCode.replace(/\s/g, '').length !== 6) {
      setError('Enter the 6-digit code from your authenticator app');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/auth/mfa/verify-login', {
        mfa_token: mfaToken,
        code: mfaCode.replace(/\s/g, ''),
        trust_device: trustDevice,
        label: `${navigator.platform || 'Device'} · ${(navigator.userAgent || '').slice(0, 80)}`,
      });
      if (data.device_token) {
        try { localStorage.setItem('device_tokens', JSON.stringify({ ...deviceTokens(), [loginKey()]: data.device_token })); } catch { /* storage unavailable */ }
      }
      setAuth(data.user, data.access_token);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: 'radial-gradient(1200px 600px at 20% -10%, #3B82F6 0%, transparent 55%), linear-gradient(135deg, #1E40AF 0%, #2563EB 45%, #0EA5E9 120%)',
      p: 2,
    }}>
      <Card sx={{ width: 410, borderRadius: 3, boxShadow: '0 20px 60px rgba(2,20,60,0.35)' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', bgcolor: step === 'credentials' ? '#fff' : 'success.main', border: step === 'credentials' ? '1px solid #EEF1F6' : 'none', borderRadius: 2.5, px: step === 'credentials' ? 2 : 1.5, py: 1.25, mb: 2, boxShadow: '0 6px 16px rgba(37,99,235,0.20)' }}>
              {step === 'credentials' ? <img src="/synergytalk-logo.svg" alt="CallEssential" style={{ height: 34, display: 'block' }} /> : <Lock sx={{ color: 'white', fontSize: 32 }} />}
            </Box>
            <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: '-0.01em' }}>
              {step === 'mfa' ? 'Two-Factor Authentication' : step === 'setup' ? 'Set Up Two-Factor Auth' : 'CallEssential Edge'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {step === 'mfa' ? 'Enter the code from your authenticator app'
                : step === 'setup' ? 'Your administrator requires 2FA on this account'
                : 'Carrier-grade Session Border Controller'}
            </Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {step === 'credentials' ? (
            <form onSubmit={handleCredentials}>
              <TextField
                fullWidth label="Email or Username" margin="normal" value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                autoFocus autoComplete="username"
              />
              <TextField
                fullWidth label="Password" type={showPass ? 'text' : 'password'} margin="normal"
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPass(!showPass)} size="small">
                        {showPass ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button fullWidth type="submit" variant="contained" size="large"
                sx={{ mt: 2, py: 1.5, borderRadius: 2 }} disabled={loading}>
                {loading ? <CircularProgress size={20} color="inherit" /> : 'Sign In'}
              </Button>
            </form>
          ) : step === 'mfa' ? (
            <form onSubmit={handleMfa}>
              <TextField
                fullWidth label="6-digit authenticator code" margin="normal"
                value={mfaCode}
                onChange={e => setMfaCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', style: { letterSpacing: '0.4em', fontSize: 22, textAlign: 'center' } }}
                autoFocus
                helperText="Open Google Authenticator / Authy and enter the current code"
              />
              <FormControlLabel
                sx={{ mt: 0.5 }}
                control={<Checkbox checked={trustDevice} onChange={e => setTrustDevice(e.target.checked)} size="small" />}
                label={<Typography variant="body2" color="text.secondary">Trust this device for 30 days (skip 2FA here)</Typography>}
              />
              <Button fullWidth type="submit" variant="contained" color="success" size="large"
                sx={{ mt: 1, py: 1.5, borderRadius: 2 }} disabled={loading || mfaCode.length !== 6}>
                {loading ? <CircularProgress size={20} color="inherit" /> : 'Verify'}
              </Button>
              <Divider sx={{ my: 2 }} />
              <Button fullWidth startIcon={<ArrowBack />} onClick={() => { setStep('credentials'); setMfaCode(''); setError(''); setMfaToken(''); }}
                color="inherit" variant="text" size="small">
                Back to login
              </Button>
            </form>
          ) : (
            <form onSubmit={handleEnableForced}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Scan this QR code with Google Authenticator / Authy, then enter the 6-digit code to finish.
              </Typography>
              {qrCode && (
                <Box sx={{ textAlign: 'center', mb: 1.5 }}>
                  <Box component="img" src={qrCode} alt="2FA QR code" sx={{ width: 180, height: 180, border: '1px solid #EEF1F6', borderRadius: 2, p: 1, bgcolor: '#fff' }} />
                </Box>
              )}
              {setupSecret && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 1, wordBreak: 'break-all' }}>
                  Can't scan? Enter this key manually: <b>{setupSecret}</b>
                </Typography>
              )}
              <TextField
                fullWidth label="6-digit code from your app" margin="normal"
                value={mfaCode}
                onChange={e => setMfaCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', style: { letterSpacing: '0.4em', fontSize: 22, textAlign: 'center' } }}
                autoFocus
              />
              <Button fullWidth type="submit" variant="contained" color="success" size="large"
                sx={{ mt: 2, py: 1.5, borderRadius: 2 }} disabled={loading || mfaCode.length !== 6}>
                {loading ? <CircularProgress size={20} color="inherit" /> : 'Enable & Sign In'}
              </Button>
              <Divider sx={{ my: 2 }} />
              <Button fullWidth startIcon={<ArrowBack />} onClick={() => { setStep('credentials'); setMfaCode(''); setError(''); setSetupToken(''); setQrCode(''); }}
                color="inherit" variant="text" size="small">
                Back to login
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', mt: 2.5 }}>
        © {new Date().getFullYear()} CallEssential · Edge SBC Platform
      </Typography>
    </Box>
  );
};
